import React, { useCallback } from 'react';
import { useRouteMatch } from 'react-router-dom';
import {
  AppButton,
  Box,
  Preloader,
  RoundPendingIcon,
  Text,
} from '@common-fe/common-fe';

import ROUTES from '@/common/routes';
import { ModalWrapper } from '@/components';
import { useHistory } from '@/modules/core/hooks';
import { QueryKeys } from '@/modules/core/hooks/useHistory';
import { getIdsFromPath } from '@/utils';

import { ExpressSetupStatuses } from './ExpressSetup.types';
import { useUpdateExpressSetupStatusByIdQuery } from './queries';

export const headerText = 'By opening the advanced setup, neither you nor the employer admin will have access to express setup';
export const description = 'Please note that the organization cannot be activated without a main contact.';

interface Props {
  visible: boolean;
  orgPath?: string
  onSetVisible: (visible: boolean) => void;
}

export const MoveToAdvanceSetupModal: React.FC<Props> = ({ visible, orgPath, onSetVisible }) => {
  const { params: { id } } = useRouteMatch<{ id: string }>();
  const history = useHistory();
  const { handleSave, isLoading } = useUpdateExpressSetupStatusByIdQuery();

  const redirectToAdvanceSetup = useCallback(() => {
    const { partnerId, distributorId, employerId } = getIdsFromPath(orgPath);
    history.push(ROUTES.EMPLOYERS_EDIT(id), 
      {
        [QueryKeys.OBSERVING_PARTNER_QUERY_KEY]: partnerId,
        [QueryKeys.OBSERVING_DISTRIBUTOR_QUERY_KEY]: distributorId,
        [QueryKeys.OBSERVING_EMPLOYER_QUERY_KEY]: employerId,
      });
  }, [history, id, orgPath]);

  const handleMoveToAdvanceSetup = useCallback(async() => {
    await handleSave({ id, status: ExpressSetupStatuses.ADVANCED_SETUP });
    onSetVisible(false);
    redirectToAdvanceSetup();
  }, [handleSave, id, onSetVisible, redirectToAdvanceSetup]);
  return (
    <ModalWrapper testId="MoveToAdvanceSetupModal" visible={visible} onSetVisible={onSetVisible}>
      <Box align="center" pad="spacing24">
        <RoundPendingIcon color="warning" />
        <Text
          data-testid="MoveToAdvanceSetupModal_modal_header"
          margin={{ vertical: 'spacing12' }}
          textAlign="center"
          size="24px"
          weight={700}
          color="textBody"
        >
          {headerText}
        </Text>
        <Box pad={{ horizontal: 'spacing24' }}>
          <Text
            data-testid="MoveToAdvanceSetupModal_modal_help-text"
            textAlign="center"
            color="textBody"
            margin={{ bottom: 'spacing24' }}
          >
            {description}
          </Text>

        </Box>
        <Box direction="row" gap="spacing12">
          <AppButton
            buttonType="secondary"
            testId="MoveToAdvanceSetupModal_modal_cancel"
            onClick={() => onSetVisible(false)}
            width="200px"
          >
            Cancel
          </AppButton>
          <AppButton
            color="red"
            testId="MoveToAdvanceSetupModal_modal"
            onClick={handleMoveToAdvanceSetup}
            width="200px"
          >
            {isLoading
              ? <Preloader />
              : 'Yes, Use Advanced Setup'}
          </AppButton>
        </Box>
      </Box>
    </ModalWrapper>
  );
};
